import {
  ClassificationParameters,
  ClassificationParametersPage,
  Polygon,
  RobotPreview,
  RobotSentence,
  RobotWordClick,
  RobotWordClickFromSmartCanvasClick,
  RobotWordClickXPath,
  RobotXPATHSentence,
} from 'src/modules/shared/models/robots.model';
import {
  IRobotSentence,
  TRobotConstantFields,
  TDocumentSentencesAndWordClicks,
} from 'src/modules/shared/models/interfaces/robots.interface';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ISmartCanvasClickedItemEvent, ISmartCanvasClickedNodeEvent, ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { EventEmitterService } from '../../../shared/services/event-emitter.service';
import { RobotsService } from '../../../shared/services/robots.service';
import { IRobotCreationStates } from '../../models/robots.states';
import { Subscription } from 'rxjs';
import { GroupConfiguration, GroupConfigurationKey } from 'src/modules/shared/models/group-keys.model';
import { environment } from 'src/environments/environment';
import { IFileDocument, IFilePageAndWords } from 'src/modules/shared/models/interfaces/files.interface';
import { faArrowUpRightFromSquare, faCircleXmark, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { SmartCanvasHighlightedArea } from 'src/modules/shared/models/smart-canvas.model';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { IGroupConfigurationKey } from 'src/modules/shared/models/interfaces/group-keys.interface';
import {
  ClassificationParameterTypeEnum,
  IClassificationParameters,
} from 'src/modules/shared/models/interfaces/classification-parameters.interface';
import { map } from 'rxjs/operators';
import { FilterExistingRobotsPipe } from '../../pipes/filter-existing-robots.pipe';
import { SentenceTypeEnum } from 'src/modules/shared/models/enums/sentence-type.enum';

@Component({
  selector: 'hcr-robot-creation-form',
  templateUrl: './robot-creation-form.component.html',
  styleUrls: ['./robot-creation-form.component.sass'],
})
export class RobotCreationFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedRobotFiles!: IFileDocument[];
  @Input() selectedRobot!: ClassificationParameters | null;
  @Input() selectedRobotId?: string;
  @Input() smartCanvasOptions!: ISmartCanvasOptions;
  @Input() selectedFilePagesAndWords!: IFilePageAndWords[];
  @Input() robotsGroups!: GroupConfiguration[];
  @Input() selectedGroupConfiguration!: GroupConfiguration;
  @Input() selectedDocumentSentencesAndWordClicks: TDocumentSentencesAndWordClicks;
  @Input() selectedRobotFilePreview!: RobotPreview;
  @Input() loadingPreview!: boolean;
  @Output() selectedGroupConfigurationChange = new EventEmitter<GroupConfiguration>();
  @Output() stateChange = new EventEmitter<{ stateChanged: string; newValue: any; states: IRobotCreationStates; data?: any }>();
  @Output() exit = new EventEmitter<void>();
  @Output() previewRobot = new EventEmitter<TDocumentSentencesAndWordClicks>();

  private _limitToShow = 5;

  public mouseEnterEvent: EventEmitter<RobotSentence | RobotWordClick> = new EventEmitter();
  public mouseLeaveEvent: EventEmitter<void> = new EventEmitter();
  public robotName: string = null;
  public filteredCustomRobots: IClassificationParameters[] = [];
  public states: IRobotCreationStates;
  public errorMessage: string;
  public pinnedGroupKeys: { [key: string]: { group: GroupConfigurationKey; value: string } } = {};
  public groupConfigurationInputs: GroupConfigurationKey[] = [];
  public selectedGroupKey: GroupConfigurationKey;
  public customRobots: IClassificationParameters[] = [];
  public robotValidationErrorMessage: string;
  public limitToShow = this._limitToShow;
  public classificationParametersPages: ClassificationParametersPage[] = [];
  public selectedDocumentIndex = 0;
  public classificationParametersTypes = ClassificationParameterTypeEnum;

  public icons = {
    exclamationTriangle: faExclamationTriangle,
    plus: faPlus,
    arrowUpRightFromSquare: faArrowUpRightFromSquare,
    xMark: faCircleXmark,
  };

  private _subscriptions: { [key: string]: Subscription } = {};

  public get hasInvalidThreshold(): boolean {
    let hasInvalidValue: boolean;
    for (let i = 0; i < this.classificationParametersPages.length; i++) {
      const wordClick = this.classificationParametersPages[i].wordClicks.findIndex((wc) => wc.extractionThreshold > 1) > -1;
      const sentence = this.classificationParametersPages[i].sentences.findIndex((stc) => stc.identificationThreshold > 1) > -1;
      if (wordClick || sentence) {
        hasInvalidValue = true;
        break;
      } else {
        hasInvalidValue = false;
      }
    }
    return hasInvalidValue;
  }

  public get canSaveRobot(): boolean {
    return (
      !this.states.saving && this.selectedDocumentIndex === this.classificationParametersPages?.length - 1 && !this.hasInvalidThreshold
    );
  }

  public canShowRobotName(): boolean {
    return this.selectedRobotId !== environment.niboObligationsRobotId && this.selectedDocumentIndex === 0;
  }

  public get isNiboDocsRobot(): boolean {
    return this.selectedRobotId === environment.niboDocsRobotId;
  }

  public get isInvalidRobotName(): boolean {
    if (this.robotName?.trim().length < 3) {
      this.robotValidationErrorMessage = null;
      return true;
    } else if (this.customRobots.some((robots) => robots?.name.trim().toLowerCase() === this.robotName?.trim().toLowerCase())) {
      this.robotValidationErrorMessage = 'Já existe um robô com este nome.';
      return true;
    } else {
      this.robotValidationErrorMessage = null;
      return false;
    }
  }

  public get isValidSentenceForm(): boolean {
    return (
      this.selectedGroupConfiguration &&
      !this.isInvalidRobotName &&
      (this.selectedGroupConfiguration.type === ClassificationParameterTypeEnum.xPath
        ? this.classificationParametersPages[this.selectedDocumentIndex]?.sentences.length > 0
        : this.classificationParametersPages[this.selectedDocumentIndex]?.sentences.length > 2)
    );
  }

  public get actualFilteredRobotsQuantity(): number {
    return this.filteredCustomRobots.length;
  }

  constructor(private _eventEmitterService: EventEmitterService, private _robotsService: RobotsService, private _toastr: ToastrService) {
    this.states = {
      saving: false,
      loading: false,
      finished: false,
      step: 0,
    };

    this.stateChange.subscribe((event) => {
      this.states[event.stateChanged] = event.newValue;
    });

    this.classificationParametersPages = [
      new ClassificationParametersPage({
        isComplementary: false,
        sentences: [],
        wordClicks: [],
      }),
    ];
  }

  public onMouseEnterSelectedWordClick(input: GroupConfigurationKey): void {
    const wordClick = this.classificationParametersPages[this.selectedDocumentIndex].wordClicks.find(
      (wordClick) => wordClick.word.key === input.key
    );
    this.mouseEnterEvent.emit(wordClick);
  }

  public onChangeSentenceSliderValue(sentence: IRobotSentence, value: number): void {
    this.previewSentence({ ...sentence, identificationThreshold: value });
  }

  public onChangeWordSliderValue(eventPayload: { value: number; selectedWord: RobotWordClick }): void {
    this.previewWordClicks(eventPayload.selectedWord, eventPayload.value);
  }

  public onHighlightInput(input: GroupConfigurationKey): void {
    this.selectedGroupKey = input;
  }

  public onRemoveHighlightInput(): void {
    this.selectedGroupKey = null;
  }

  // Atualmente só serve para NiboDocs
  public getCustomRobots(): void {
    if (this.isNiboDocsRobot) {
      this._robotsService
        .getCustomRobots(this.selectedRobotId)
        .pipe(
          map((robots: IClassificationParameters[]) => {
            return robots.filter((robot: IClassificationParameters) => robot.name?.trim().length > 0 && robot?.name !== this.robotName);
          })
        )
        .subscribe((robots: IClassificationParameters[]) => {
          this.customRobots = robots;
        });
    }
  }

  public loadMoreRobots(): void {
    this.limitToShow += this._limitToShow;
  }

  public filterCustomRobots(): void {
    this.filteredCustomRobots = new FilterExistingRobotsPipe().transform(this.customRobots, this.robotName);
    if (this.actualFilteredRobotsQuantity === 0) this.limitToShow = this._limitToShow;
  }

  public getDocumentName(index: number): string {
    return index == 0 ? 'Principal' : this.classificationParametersPages[index].isComplementary ? `Complemento` : `Documento ${index}`;
  }

  public nextStep(): void {
    if (this.selectedDocumentIndex < this.classificationParametersPages.length - 1 && this.states.step === 2) {
      this.changeDocument(this.selectedDocumentIndex + 1);
    } else {
      this.stateChange.next({ stateChanged: 'step', newValue: this.states.step + 1, states: this.states });
    }
  }

  public previousStep(): void {
    if (this.selectedDocumentIndex === this.classificationParametersPages.length - 1 && this.states.step === 1) {
      this.changeDocument(this.selectedDocumentIndex - 1);
      this.stateChange.next({ stateChanged: 'step', newValue: 2, states: this.states });
    } else {
      this.stateChange.next({ stateChanged: 'step', newValue: this.states.step - 1, states: this.states });
    }
  }

  public removeSentence(index: number, documentIndex = this.selectedDocumentIndex): void {
    const sentence = this.classificationParametersPages[documentIndex].sentences[index];
    this._eventEmitterService.get<number>('remove-canvas-highlight').emit(sentence._id);
    this.classificationParametersPages[documentIndex].sentences.splice(index, 1);
    this.firePreviewRobot();
  }

  public updateSentence(index: number, sentence: IRobotSentence, documentIndex = this.selectedDocumentIndex): void {
    Object.keys(this.classificationParametersPages[documentIndex].sentences[index]).forEach((key) => {
      this.classificationParametersPages[documentIndex].sentences[index][key] = sentence[key];
    });
    this.previewSentence(sentence);
    this.firePreviewRobot();
  }

  public previewSentence(sentence: IRobotSentence): void {
    this._eventEmitterService.get<SmartCanvasHighlightedArea>('update-canvas-highlight').emit(
      new SmartCanvasHighlightedArea(
        `sentences-${this.selectedDocumentIndex}`,
        new RobotSentence(sentence),
        {
          fill: this.smartCanvasOptions.customStyles.sentences.fill,
          opacity: this.smartCanvasOptions.customStyles.sentences.opacity,
        },
        sentence.identificationThreshold
      )
    );
  }

  public previewWordClicks(selectedWord: RobotWordClick, value?: number): void {
    this.selectedFilePagesAndWords.forEach((_, index) => {
      this._eventEmitterService.get<SmartCanvasHighlightedArea>('update-canvas-highlight').emit(
        new SmartCanvasHighlightedArea(
          `wordclicks-${index}`,
          selectedWord,
          {
            fill: this.smartCanvasOptions.customStyles.wordClicks.fill,
            opacity: this.smartCanvasOptions.customStyles.wordClicks.opacity,
          },
          value ? value : selectedWord.extractionThreshold
        )
      );
    });
  }

  public addXpathSentence(xPath = '', documentIndex = this.selectedDocumentIndex): void {
    this.classificationParametersPages[documentIndex].sentences.push(new RobotXPATHSentence(xPath));
  }

  public addSentence(sentence: RobotSentence, dispatchEvent = true, documentIndex = this.selectedDocumentIndex): void {
    if (!this.classificationParametersPages[documentIndex].sentences.find((s) => s.text === sentence.text)) {
      this.classificationParametersPages[documentIndex].sentences.push(sentence);

      if (!dispatchEvent) {
        return;
      }

      this.firePreviewRobot();

      if (sentence.type !== SentenceTypeEnum.XPath) {
        this._eventEmitterService.get<SmartCanvasHighlightedArea>('add-canvas-highlight').emit(
          new SmartCanvasHighlightedArea(
            `sentences-${this.selectedDocumentIndex}`,
            sentence,
            {
              fill: this.smartCanvasOptions.customStyles.sentences.fill,
              opacity: this.smartCanvasOptions.customStyles.sentences.opacity,
            },
            sentence.identificationThreshold
          )
        );
      }
    }
  }

  public pinGroupKey(config: { pinned: boolean; key: GroupConfigurationKey; value: string }): void {
    this.clearInput(config.key);

    if (config.pinned) {
      this.pinnedGroupKeys[config.key.key] = { group: config.key, value: config.value };
    }
  }

  public clearInput(groupKey: GroupConfigurationKey, documentIndex = this.selectedDocumentIndex): void {
    const selectedWordIndex = this.classificationParametersPages[documentIndex].wordClicks.findIndex(
      (click) => click.word.key === groupKey.key
    );

    if (selectedWordIndex !== -1) {
      this._eventEmitterService
        .get<number>('remove-canvas-highlight')
        .emit(this.classificationParametersPages[documentIndex].wordClicks[selectedWordIndex]._id);

      this.classificationParametersPages[documentIndex].wordClicks.splice(selectedWordIndex, 1);
      delete this.pinnedGroupKeys[groupKey.key];
    }

    this.firePreviewRobot();
  }

  public firePreviewRobot(): void {
    this.previewRobot.emit({
      sentences: this.classificationParametersPages[this.selectedDocumentIndex].sentences,
      wordClicks: this._getWordClicksWithValidLandmarks(),
    });
  }

  public saveRobot(): void {
    this.stateChange.next({ stateChanged: 'saving', newValue: true, states: this.states });

    const classificationParameters = new ClassificationParameters();
    classificationParameters.state = this.selectedRobot?.state || {};
    classificationParameters.robotId = this.selectedRobotId;
    classificationParameters.fileIds = this.selectedRobotFiles.map((file) => file.id);
    classificationParameters.name = this.robotName;
    classificationParameters.wordClicks = this.classificationParametersPages[0].wordClicks;
    classificationParameters.sentences = this.classificationParametersPages[0].sentences;
    classificationParameters.type = this.selectedGroupConfiguration.type;

    this.classificationParametersPages.forEach((page, index) => {
      const selectedConfig = this.robotsGroups.find((group) => group.id === page.getStateItem('groupId'));

      if (index === 0) {
        Object.entries(selectedConfig.state).forEach((key) => {
          classificationParameters.addStateItem(key[0], key[1]);
        });

        classificationParameters.addStateItem(
          'customFields',
          selectedConfig.keys?.filter((key) => key.isCustom)
        );

        classificationParameters.addStateItem('groupId', selectedConfig.id);
      } else if (selectedConfig) {
        Object.entries(selectedConfig.state).forEach((key) => {
          page.addStateItem(key[0], key[1]);
        });

        page.addStateItem(
          'customFields',
          selectedConfig.keys.filter((key) => key.isCustom)
        );

        page.addStateItem('groupId', selectedConfig.id);
      }
    });

    /// Especialização para robôs do tipo NIBO OBRIGAÇÕES GLOBAIS
    if (this.selectedRobotId === environment.niboObligationsRobotId) {
      classificationParameters.name = this.selectedGroupConfiguration.name;
    }

    /// TODO: Valores fixos ainda não são suportados por robôs multi-documentos
    classificationParameters.addStateItem(
      'constantFields',
      Object.keys(this.pinnedGroupKeys).reduce<TRobotConstantFields>((values, field) => {
        return { ...values, [field]: this.pinnedGroupKeys[field].value };
      }, {})
    );

    classificationParameters.pages = this.classificationParametersPages.filter((_, index) => index > 0);

    const errorOnSave = () => {
      this.stateChange.next({ stateChanged: 'saving', newValue: false, states: this.states });
      this._toastr.error('Ops! Ocorreu um erro ao salvar esse robô. Tente novamente mais tarde.');
    };

    if (this.selectedRobot) {
      classificationParameters.classificationId = this.selectedRobot.classificationId;
      this._robotsService.editCustomRobot(this.selectedRobot.id, classificationParameters).subscribe((result) => {
        this.stateChange.next({ stateChanged: 'saving', newValue: false, states: this.states });
        this.stateChange.next({ stateChanged: 'finished', newValue: true, states: this.states, data: result });
        this.nextStep();
      }, errorOnSave);
    } else {
      this._robotsService.createCustomRobot(classificationParameters).subscribe((result) => {
        this.stateChange.next({ stateChanged: 'saving', newValue: false, states: this.states });
        this.stateChange.next({ stateChanged: 'finished', newValue: true, states: this.states, data: result });
        this.nextStep();
      }, errorOnSave);
    }
  }

  /**
   * @deprecated Função usada apenas para, quando editar um robô de um cliente do Nibo Obrigações
   */
  public niboObligationsReferenceId(): string | null {
    try {
      if (this.selectedRobot && this.selectedRobot.state) {
        const jsonState = JSON.parse(this.selectedRobot.state);
        return jsonState ? jsonState['ObligationMasterDocReferenceId'] : null;
      }
    } catch (e) {
      console.error('Error parsing state', e);
    }

    return null;
  }

  public loadGroupConfiguration(groupId: string): void {
    if (this.robotsGroups && groupId) {
      this.selectedGroupConfiguration = this.robotsGroups.find((v) => v.id === groupId);

      if (this.selectedGroupConfiguration) {
        this.selectedGroupConfigurationChange.emit(this.selectedGroupConfiguration);
        this.groupConfigurationInputs = this.selectedGroupConfiguration.keys;
      }

      this.classificationParametersPages[this.selectedDocumentIndex].addStateItem('groupId', groupId);

      /// Lê possíveis campos personalizados pelo usuário
      if (this.selectedRobot?.state) {
        const state = JSON.parse(this.selectedRobot.state);
        const customFields = state['customFields'];

        if (customFields) {
          customFields.forEach((customField: IGroupConfigurationKey) => {
            if (!this.groupConfigurationInputs.find((group) => group.key === customField.key)) {
              this.addCustomWordInput(new GroupConfigurationKey(customField));
            }
          });
        }
      }

      this.selectedRobot?.wordClicks.forEach((wordClick) => {
        if (!wordClick.landmarks) {
          wordClick.landmarks = [];
        }
      });

      if (this.selectedRobot?.state) {
        const constantFields: TRobotConstantFields = JSON.parse(this.selectedRobot.state)['constantFields'] || {};

        Object.keys(constantFields).forEach((key) => {
          this.pinGroupKey({
            pinned: true,
            key: this.selectedGroupConfiguration.keys.find((k) => k.key === key),
            value: constantFields[key],
          });
        });
      }
    }
  }

  private _getWordClicksWithValidLandmarks(): RobotWordClick[] {
    return this.classificationParametersPages[this.selectedDocumentIndex].wordClicks.map((wc) => {
      const landmarks = wc.landmarks?.filter((landmark) => {
        if (
          (landmark.sentence.type !== SentenceTypeEnum.Regex && !landmark.sentence.text) ||
          (landmark.sentence.type === SentenceTypeEnum.Regex && !landmark.sentence.pattern)
        ) {
          return null;
        }
        return landmark;
      });
      return { ...wc, landmarks };
    });
  }

  private _addWordClick(wordClick: RobotWordClick, documentIndex = this.selectedDocumentIndex): void {
    const wordClickIndex = this.classificationParametersPages[documentIndex].wordClicks.findIndex(
      (click) => click.word.key === wordClick.word.key
    );

    if (wordClickIndex !== -1) {
      if (!wordClick.xPath) {
        this._eventEmitterService
          .get<number>('remove-canvas-highlight')
          .emit(this.classificationParametersPages[documentIndex].wordClicks[wordClickIndex]._id);
      }

      this.classificationParametersPages[documentIndex].wordClicks.splice(wordClickIndex, 1);
    }

    this.classificationParametersPages[documentIndex].wordClicks.push(wordClick);
    this.firePreviewRobot();

    if (!wordClick.xPath) {
      this._eventEmitterService.get<SmartCanvasHighlightedArea>('add-canvas-highlight').emit(
        new SmartCanvasHighlightedArea(
          `wordclicks-${this.selectedDocumentIndex}`,
          wordClick,
          {
            fill: this.smartCanvasOptions.customStyles.wordClicks.fill,
            opacity: this.smartCanvasOptions.customStyles.wordClicks.opacity,
          },
          wordClick.extractionThreshold
        )
      );
    }
  }

  public exitFromRobot(): void {
    this.exit.emit();
  }

  public addCustomWordInput(customField?: GroupConfigurationKey): void {
    if (!customField) {
      customField = new GroupConfigurationKey({
        isCustom: true,
        key: null,
        label: null,
        type: null,
      });
    }

    this.groupConfigurationInputs.push(customField);
  }

  public removeCustomInput(groupKey: IGroupConfigurationKey): void {
    this.groupConfigurationInputs = this.groupConfigurationInputs.filter((v) => v.key !== groupKey.key);
    this.selectedGroupConfiguration.keys = this.groupConfigurationInputs;
    this.classificationParametersPages.forEach((page) => {
      page.wordClicks = page.wordClicks.filter((click) => click.word.key !== groupKey.key);
    });
  }

  public addDocument(): void {
    const newPage = new ClassificationParametersPage();
    newPage.addStateItem('groupId', this.selectedGroupConfiguration.id);
    this.classificationParametersPages.push(newPage);
    this.changeDocument(this.classificationParametersPages.length - 1);
  }

  public removeDocument(index: number, event: MouseEvent): void {
    event.stopPropagation();

    if (this.selectedDocumentIndex >= index) {
      this.changeDocument(this.selectedDocumentIndex - 1);
    }

    this.classificationParametersPages.splice(index, 1);

    this._eventEmitterService.get<string>('remove-canvas-group-highlight').emit(`sentences-${index}`);
    this._eventEmitterService.get<string>('remove-canvas-group-highlight').emit(`wordclicks-${index}`);
  }

  public changeDocument(index: number): void {
    this.selectedDocumentIndex = index;
    this.stateChange.next({ stateChanged: 'step', newValue: 1, states: this.states });
    this.loadGroupConfiguration(this.classificationParametersPages[index].getStateItem<string>('groupId'));

    this.classificationParametersPages.forEach((page, i) => {
      if (index === i) {
        this._eventEmitterService.get<string>('show-canvas-group-highlight').emit(`wordclicks-${i}`);
        this._eventEmitterService.get<string>('show-canvas-group-highlight').emit(`sentences-${i}`);
      } else {
        this._eventEmitterService.get<string>('hide-canvas-group-highlight').emit(`wordclicks-${i}`);
        this._eventEmitterService.get<string>('hide-canvas-group-highlight').emit(`sentences-${i}`);
      }
    });

    this.previewRobot.emit({
      sentences: this.classificationParametersPages[this.selectedDocumentIndex].sentences,
      wordClicks: this.classificationParametersPages[this.selectedDocumentIndex].wordClicks,
    });
  }

  ngOnInit(): void {
    this.getCustomRobots();

    this.mouseEnterEvent.subscribe((data) => {
      this._eventEmitterService
        .get<{ data: RobotSentence | RobotWordClick; selectedDocumentIndex: number }>('fortify-canvas-highlight')
        .emit({ data, selectedDocumentIndex: this.selectedDocumentIndex });
    });

    this.mouseLeaveEvent.subscribe(() => {
      this._eventEmitterService.get<void>('weaken-canvas-highlight').emit();
    });

    this._subscriptions['clicked-node-value'] = this._eventEmitterService
      .get<ISmartCanvasClickedNodeEvent>('clicked-node-value')
      .subscribe((result) => {
        if (!this.states.saving) {
          switch (this.states.step) {
            case 1:
              this.addSentence(
                new RobotSentence({
                  text: result.xPath,
                  xPath: result.xPath,
                  type: SentenceTypeEnum.XPath,
                  isDenied: false,
                })
              );
              break;
            case 2:
              if (this.selectedGroupKey) {
                this._addWordClick(new RobotWordClickXPath(result.xPath, this.selectedGroupKey));
              }
              break;
          }
        }
      });

    this._subscriptions['clicked-value'] = this._eventEmitterService
      .get<ISmartCanvasClickedItemEvent>('clicked-value')
      .subscribe((result) => {
        console.log('Robot: Clicked value', result);
        if (!this.states.saving) {
          switch (this.states.step) {
            case 1:
              result.selectedArea?.polygonsInside.forEach((polygon) => {
                this.addSentence(
                  new RobotSentence({
                    text: polygon.content.text,
                    polygon: new Polygon(polygon.content.boundingBoxRelative),
                    type: SentenceTypeEnum.Default,
                    isDenied: false,
                  })
                );
              });
              break;
            case 2:
              if (result.groupKey.isCustom && !this.groupConfigurationInputs.find((group) => group.key === result.groupKey.key)) {
                this.addCustomWordInput(result.groupKey);
              }

              this._addWordClick(new RobotWordClickFromSmartCanvasClick(result));
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedRobot?.currentValue && changes.selectedRobot.currentValue !== changes.selectedRobot.previousValue) {
      if (this.selectedRobot) {
        this.states.step = 1;
        this.stateChange.next({ stateChanged: 'step', newValue: 1, states: this.states });
        this.stateChange.next({ stateChanged: 'loading', newValue: true, states: this.states });
        this.robotName = this.selectedRobot.name;

        this.classificationParametersPages = [
          new ClassificationParametersPage({
            isComplementary: false,
            sentences: this.selectedRobot.sentences,
            wordClicks: this.selectedRobot.wordClicks,
            state: this.selectedRobot.state,
          }),
        ];

        this.selectedRobot.pages?.forEach((page) => {
          this.classificationParametersPages.push(new ClassificationParametersPage(page));
        });

        this.selectedRobot.sentences.forEach((sentence) => {
          this.addSentence(sentence, false);
        });

        this.stateChange.next({ stateChanged: 'loading', newValue: false, states: this.states });
        try {
          if (this.selectedRobot.state) {
            const robotState: { [id: string]: string } = JSON.parse(this.selectedRobot.state);

            if (robotState && robotState['groupId']) {
              this.loadGroupConfiguration(robotState['groupId']);
            }
          }
        } catch (e) {
          console.error('Error parsing state', e);
        }
      } else {
        this.selectedRobot = new ClassificationParameters();

        this.classificationParametersPages = [
          new ClassificationParametersPage({
            isComplementary: false,
            sentences: [],
            wordClicks: [],
          }),
        ];
      }
    }
  }

  ngOnDestroy(): void {
    for (const subscription in this._subscriptions) {
      this._subscriptions[subscription].unsubscribe();
    }
  }
}
